import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = process.env.REACT_APP_API_URL;

// Function to fetch user profile from the server
export const getProfile = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/profile`, { headers });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch profile');
    }
    
    const data = await response.json();
    return data.profile || {};
  } catch (error) {
    console.error('Error fetching profile:', error);
    throw error;
  }
};

// Function to update user profile
export const updateProfile = async (profileData) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/profile`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ profile: profileData }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update profile');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

// Function to update profile with picture
export const updateProfileWithPicture = async (profileData, file) => {
  try {
    // Convert the file to base64
    const base64 = await fileToBase64(file);
    
    // Add the base64 data to the profile
    const updatedProfileData = {
      ...profileData,
      profilePictureBase64: base64
    };
    
    // Use the regular update profile function
    return updateProfile(updatedProfileData);
  } catch (error) {
    console.error('Error updating profile with picture:', error);
    throw error;
  }
};

// Helper function to convert a file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

// Helper function to get auth headers
const getAuthHeaders = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    const token = tokens.idToken.toString();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  } catch (error) {
    console.error('Error getting auth token:', error);
    throw error;
  }
};

// Function to call GET /hello endpoint
export const getHelloMessage = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/hello`, { headers });
    const data = await response.json();
    return data.message;
  } catch (error) {
    console.error('Error fetching hello message:', error);
    return 'Error getting hello message';
  }
};

// Function to call POST /message endpoint
export const postMessage = async (message) => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/message`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to send message');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error posting message:', error);
    throw error;
  }
};

// Function to fetch chat messages from the server
export const getChatMessages = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/messages`, { headers });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch messages');
    }
    
    const data = await response.json();
    return data.messages || [];
  } catch (error) {
    console.error('Error fetching chat messages:', error);
    throw error;
  }
};

// Function to call GET /info endpoint
export const getInfo = async () => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/info`, { headers });
    const data = await response.json();
    return data.info;
  } catch (error) {
    console.error('Error fetching info:', error);
    return 'Error getting info';
  }
};
